import React, { useState, useMemo } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { motion, AnimatePresence } from "framer-motion";
import { usePhotoActions } from "data/usePhotoActions";
import { getAbsoluteUrl } from "utils/urlUtils";
import { ReactComponent as HeartIcon } from "feather-icons/dist/icons/heart.svg";
import { ReactComponent as ThumbsDownIcon } from "feather-icons/dist/icons/thumbs-down.svg";
import { ReactComponent as ChevronLeftIcon } from "feather-icons/dist/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "feather-icons/dist/icons/chevron-right.svg";
import { ReactComponent as XIcon } from "feather-icons/dist/icons/x.svg";

const Overlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-75 z-40 flex items-center justify-center p-4`}
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled(motion.div)`
  ${tw`bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-full md:max-h-full flex flex-col overflow-hidden relative z-50`}
`;

const ImageSection = tw.div`flex-1 flex items-center justify-center bg-gray-900 relative overflow-hidden`;

const MainImage = styled.img`
  ${tw`w-full h-full object-contain`}
`;

const ImageControls = styled.div`
  ${tw`absolute inset-0 flex items-center justify-between p-4 pointer-events-none`}
`;

const NavigationButton = styled.button`
  ${tw`absolute top-1/2 transform -translate-y-1/2 bg-primary-500 hover:bg-primary-600 text-white p-3 rounded-full transition-colors duration-200 pointer-events-auto`}
  z-index: 10;

  &:disabled {
    ${tw`bg-gray-500 cursor-not-allowed`}
  }
`;

const PrevButton = styled(NavigationButton)`
  left: 1rem;
`;

const NextButton = styled(NavigationButton)`
  right: 1rem;
`;

const CloseButton = styled.button`
  ${tw`absolute  bg-gray-800 hover:bg-gray-900 text-white p-2 rounded-full transition-colors duration-200`}
  z-index: 20;
`;

const CloseIcon = styled(XIcon)`
  ${tw`w-6 h-6`}
`;

const InfoSection = styled.div`
  ${tw`flex flex-col md:flex-row md:items-center md:justify-between p-6 bg-white border-t`}
`;

const PhotoInfo = tw.div`flex-1`;
const PhotoTitle = tw.h2`text-2xl font-bold text-gray-900 mb-2`;
const PhotoMetadata = tw.p`text-sm text-gray-600`;

const ActionButtons = styled.div`
  ${tw`flex gap-4 mt-4 md:mt-0`}
`;

const ActionButton = styled.button`
  ${tw`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-all duration-200`}

  &:disabled {
    ${tw`opacity-50 cursor-not-allowed`}
  }
`;

const LikeButton = styled(ActionButton)`
  ${tw`bg-red-500 hover:bg-red-600 text-white`}
`;

const DislikeButton = styled(ActionButton)`
  ${tw`bg-gray-500 hover:bg-gray-600 text-white`}
`;

const ThumbnailContainer = styled.div`
  ${tw`flex gap-2 mt-4 md:mt-0 md:ml-4 overflow-x-auto`}
`;

const Thumbnail = styled.img`
  ${tw`w-16 h-16 object-cover rounded cursor-pointer hover:opacity-75 transition-opacity flex-shrink-0`}
  border: 2px solid
    ${(props) =>
    props.$isActive ? tw`border-primary-500` : tw`border-transparent`};

  ${(props) =>
    props.$isActive &&
    css`
      border-color: #c78a41;
    `}
`;

const ImageCounter = styled.div`
  ${tw`text-center md:text-left text-sm text-gray-600 mt-2 md:mt-0`}
`;

/**
 * PhotoDetail Component
 * Modal for viewing photos in a set with navigation, like/dislike functionality
 */
export default function PhotoDetail({ photoSet, isOpen, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [updatedPhotos, setUpdatedPhotos] = useState({});
  const { likePhoto, dislikePhoto } = usePhotoActions();

  const photos = useMemo(() => {
    return photoSet?.photos || [];
  }, [photoSet]);

  const currentPhoto = photos[currentIndex];

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  const handleLike = async () => {
    if (currentPhoto?.id) {
      try {
        // Optimistically update local state first
        const currentLikes = getLikeCount(currentPhoto);
        setUpdatedPhotos((prev) => ({
          ...prev,
          [currentPhoto.id]: currentLikes + 1,
        }));

        await likePhoto.mutateAsync(currentPhoto.id);
      } catch (error) {
        console.error("Failed to like photo");
        // Revert on error
        setUpdatedPhotos((prev) => {
          const copy = { ...prev };
          delete copy[currentPhoto.id];
          return copy;
        });
      }
    }
  };

  const handleDislike = async () => {
    if (currentPhoto?.id) {
      try {
        await dislikePhoto.mutateAsync(currentPhoto.id);
      } catch (error) {
        console.error("Failed to dislike photo");
      }
    }
  };

  const getLikeCount = (photo) => {
    // Check if we have an updated count first
    if (updatedPhotos[photo?.id] !== undefined) {
      return updatedPhotos[photo.id];
    }
    // Fall back to the photo's likeCount, likes, or 0
    return photo?.likeCount || photo?.likes || 0;
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Overlay onClick={onClose}>
        <ModalContainer
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
        >
          {/* Image Section */}
          <ImageSection>
            {currentPhoto?.imageUrl && (
              <MainImage
                src={getAbsoluteUrl(currentPhoto.imageUrl)}
                alt={photoSet?.name}
              />
            )}
            <ImageControls>
              <PrevButton
                onClick={handlePrevious}
                disabled={photos.length <= 1}
                aria-label="Previous photo"
              >
                <ChevronLeftIcon tw="w-6 h-6" />
              </PrevButton>
              <NextButton
                onClick={handleNext}
                disabled={photos.length <= 1}
                aria-label="Next photo"
              >
                <ChevronRightIcon tw="w-6 h-6" />
              </NextButton>
            </ImageControls>
            <CloseButton onClick={onClose} aria-label="Close modal">
              <CloseIcon />
            </CloseButton>
          </ImageSection>

          {/* Info Section */}
          <InfoSection>
            <div tw="flex-1">
              <PhotoTitle>{photoSet?.name}</PhotoTitle>
              <PhotoMetadata>
                {currentPhoto && (
                  <p tw="mt-2 text-red-500 font-semibold">
                    <HeartIcon tw="w-4 h-4 inline mr-1" />
                    {getLikeCount(currentPhoto)}{" "}
                    {getLikeCount(currentPhoto) === 1 ? "like" : "likes"}
                  </p>
                )}
                {currentPhoto?.description && (
                  <p tw="mt-2">{currentPhoto.description}</p>
                )}
              </PhotoMetadata>
            </div>

            <ActionButtons>
              <LikeButton onClick={handleLike} disabled={likePhoto.isPending}>
                <HeartIcon tw="w-5 h-5" />
                Like
              </LikeButton>
            </ActionButtons>
          </InfoSection>

          {/* Thumbnails */}
          {photos.length > 1 && (
            <ThumbnailContainer>
              {photos.map((photo, index) => (
                <Thumbnail
                  key={`${photoSet?.name}-${index}`}
                  src={getAbsoluteUrl(photo.imageUrl)}
                  alt={`Thumbnail ${index + 1}`}
                  $isActive={index === currentIndex}
                  onClick={() => handleThumbnailClick(index)}
                />
              ))}
            </ThumbnailContainer>
          )}
        </ModalContainer>
      </Overlay>
    </AnimatePresence>
  );
}
